import React, { FC, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { TypeAnimation } from 'react-type-animation';

import { ReactComponent as VerificationIcon } from 'assets/icons/Verification.svg';
import { ReactComponent as BullIcon } from 'assets/icons/Bull.svg';
import { ChatType, EVisibilityStatusOptions, UserObjectType } from 'constants/graphqlTypes';
import { COLORS } from 'styles/constants';

import Avatar from 'components/UI/Avatar';
import getAuthUser from 'helpers/getAuthUser';
import useDocumentTitle from 'helpers/useDocumentTitle';
import FollowButton from 'components/FollowButton';
import AvatarPlaceholder from 'components/UI/Avatar/AvatarPlaceholder';
import {
  TEST_ID_PROFILE_VIEW_NAME_LABEL,
  TEST_ID_PROFILE_VIEW_USERNAME_LABEL,
  TEST_PROFILE_ACTION_BTN,
} from 'constants/aqa/profile';

import { EUiButtonType } from 'components/UI/Button/constants';
import { useUserSettings } from 'graphQL/contextualIntro/hooks';
import { useCreateNewChat } from 'graphQL/messages/hooks';
import { ROUTE_MESSAGES_CHAT } from 'routes';
import { transformRoute } from 'helpers/routingHelper';
import { AVATAR_DIMENSION } from './constants';
import { generateFullName } from './helpers';
import ConnectionsButton from './ConnectionsButton';
import SlotReelNumber from './SlotReelNumber';

import {
  StyledFollowButtonContainer,
  StyledFullName,
  StyledInfoContainer,
  StyledMessageButton,
  StyledTabsContainer,
  StyledTab,
  StyledText,
  StyledUserInfo,
  StyledVerifiedIcon,
  StyledGrain,
} from './styled';
import ExpandableText from './ExpandableText';

interface IInfoProps {
  noContent: boolean;
  userData?: UserObjectType;
  fontColor: string;
  hideGradient: boolean;
  onTabChange?: (tabIndex: number) => void;
  cardsCount: number;
  listsCount: number;
  gradientStart?: string | null;
  gradientEnd?: string | null;
  isDark?: boolean;
  showGradient?: boolean;
  showStyledGrain?: boolean;
}

interface ITab {
  label: string | React.ReactElement;
}

interface ITabsContainerProps {
  tabs: ITab[];
  onClick: (index: number) => void;
  currentTabIndex: number;
}

const TabsContainer: FC<ITabsContainerProps> = ({ tabs, onClick, currentTabIndex }) => {
  return (
    <StyledTabsContainer>
      {tabs.map((tab, index) => (
        <StyledTab key={`tab-${tab.label}`} onClick={() => onClick(index)}>
          {tab.label}
          {index === currentTabIndex && <BullIcon />}
        </StyledTab>
      ))}
    </StyledTabsContainer>
  );
};

const Info: FC<IInfoProps> = (props) => {
  const {
    noContent,
    userData,
    fontColor,
    hideGradient,
    onTabChange,
    cardsCount,
    listsCount,
    gradientStart,
    gradientEnd,
    isDark,
    showGradient,
    showStyledGrain,
  } = props;
  const { username, userInfo, id: userId, isBlockedByMe, isBlockedMe, isFollowingMe } = userData ?? {};
  const { firstName, lastName, bio, bioLong } = userInfo ?? {};
  const { userId: currentUserId } = getAuthUser();
  const { push } = useHistory();

  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);

  const bothReady = cardsCount !== 0 && listsCount !== 0;

  const renderDigits = (value: number) => {
    const digits = value.toString().split('');
    if (!bothReady) {
      return (
        <span style={{ display: 'inline' }}>
          {digits.map((d, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={i}>{d}</span>
          ))}
        </span>
      );
    }
    return (
      <span style={{ display: 'inline' }}>
        {digits.map((d, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i}>
            <SlotReelNumber value={parseInt(d, 10)} delay={i * 100} />
          </span>
        ))}
      </span>
    );
  };

  const tabs = useMemo(
    () => [
      {
        label: (
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            {renderDigits(cardsCount)}
            <span style={{ marginLeft: '0.25em' }}>Picks</span>
          </div>
        ),
      },
      {
        label: (
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            {renderDigits(listsCount)}
            <span style={{ marginLeft: '0.25em' }}>Lists</span>
          </div>
        ),
      },
    ],
    [cardsCount, listsCount, renderDigits]
  );

  const handleTabChange = (index: number) => {
    setCurrentTabIndex(index);
    if (onTabChange) {
      onTabChange(index);
    }
  };

  useDocumentTitle(generateFullName(userData));
  const { messagesAndThoughtsVisibility } = useUserSettings(userId);
  const isAllowedToMessage =
    messagesAndThoughtsVisibility === EVisibilityStatusOptions.Everyone ||
    (messagesAndThoughtsVisibility === EVisibilityStatusOptions.Following && isFollowingMe);

  const { createChat } = useCreateNewChat((chatId: string) =>
    push(transformRoute(ROUTE_MESSAGES_CHAT, { id: chatId }))
  );

  const [isCreateThreadDisabled, setIsCreateThreadDisabled] = useState(false);

  const handleCreateThread = () => {
    if (userId) {
      createChat([userId], ChatType.Single);
      setIsCreateThreadDisabled(true);
    }
  };

  return (
    <StyledInfoContainer
      hasWhiteAvatar={!noContent && !hideGradient}
      hasPaddingBottom={noContent}
      showGradient={showGradient}
      gradientStart={gradientStart}
      gradientEnd={gradientEnd}
      isDark={isDark}
    >
      {showStyledGrain && <StyledGrain />}
      {userData ? (
        <Avatar user={userData} dimension={AVATAR_DIMENSION} isAnimated />
      ) : (
        <AvatarPlaceholder dimension={AVATAR_DIMENSION} />
      )}
      <StyledFullName key={`${firstName}${lastName}`} data-testid={TEST_ID_PROFILE_VIEW_USERNAME_LABEL}>
        {userData ? (
          <TypeAnimation wrapper="span" cursor={false} sequence={[`${firstName} ${lastName}`, 100]} speed={35} />
        ) : (
          'Sorry, this profile cannot be found'
        )}
      </StyledFullName>
      <StyledUserInfo>
        {userData && (
          <StyledText data-testid={TEST_ID_PROFILE_VIEW_NAME_LABEL}>
            :{username}
            <StyledVerifiedIcon>
              <VerificationIcon />
            </StyledVerifiedIcon>
          </StyledText>
        )}
        {bio && <StyledText shouldBreak>{bio}</StyledText>}
        {bioLong && <ExpandableText text={bioLong} shouldBreak />}
        {username && (
          <ConnectionsButton username={username} connectionsCount={userData?.userCounters?.connectionsCount ?? 0} />
        )}
      </StyledUserInfo>
      {userId !== currentUserId && username && userData && (
        <StyledFollowButtonContainer>
          <FollowButton dataTestId={TEST_PROFILE_ACTION_BTN} user={userData} isDark={fontColor === COLORS.white[100]} />
          {!isBlockedMe && !isBlockedByMe && isAllowedToMessage && (
            <StyledMessageButton
              onClick={handleCreateThread}
              disabled={isCreateThreadDisabled}
              buttonType={EUiButtonType.Transparent}
              isDark={fontColor === COLORS.white[100]}
            >
              Message
            </StyledMessageButton>
          )}
        </StyledFollowButtonContainer>
      )}
      <TabsContainer tabs={tabs} onClick={handleTabChange} currentTabIndex={currentTabIndex} />
    </StyledInfoContainer>
  );
};

export default Info;
